"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useRouter } from "next/navigation";
import { SEARCH_INDEX, type SearchItem } from "@/lib/sections";

/**
 * Site-wide ⌘K command palette. Mounted once in the root layout. Opens on
 * ⌘K / Ctrl+K or a click on any `[data-search]` element (the header button and
 * the home search CTA both carry that attribute, so they stay decoupled from
 * this component). Filters the static section registry into investigations
 * (the read) and data sets (the record); Enter navigates the cursored row.
 *
 * Greenfield — there is no prior search UI to reconcile with.
 */
export function CommandPalette() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The cursor as last SET. Read through `cursor` below, never directly —
  // it can point past the end of a result list that has since shrunk.
  const [cursorRaw, setCursorRaw] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo<SearchItem[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SEARCH_INDEX;
    return SEARCH_INDEX.filter((it) =>
      `${it.name} ${it.desc} ${it.area}`.toLowerCase().includes(q),
    );
  }, [query]);

  const reads = results.filter((r) => r.kind === "read");
  const data = results.filter((r) => r.kind === "data");
  const ordered = useMemo(() => [...reads, ...data], [reads, data]);

  // Clamped on READ rather than corrected in an effect.
  //
  // The effect this replaces fired after the commit, so typing a character
  // that shrank the list rendered one frame with the highlight on a row that
  // no longer existed — and, for that frame, Enter would have navigated using
  // an out-of-range index. Deriving it means the cursor is never observably
  // out of range, by construction rather than by correction.
  const cursor = Math.min(cursorRaw, Math.max(0, ordered.length - 1));

  const close = useCallback(() => setOpen(false), []);
  const openPalette = useCallback(() => {
    setQuery("");
    setCursorRaw(0);
    setOpen(true);
  }, []);

  // ⌘K / Ctrl+K toggle, plus delegated clicks from any [data-search] trigger.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    function onClick(e: MouseEvent) {
      const target = e.target;
      if (target instanceof Element && target.closest("[data-search]")) {
        e.preventDefault();
        openPalette();
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onClick);
    };
  }, [openPalette]);

  // Body scroll lock + focus the input while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => {
      document.body.style.overflow = prev;
      clearTimeout(t);
    };
  }, [open]);

  const go = useCallback(
    (item: SearchItem | undefined) => {
      if (!item) return;
      close();
      router.push(item.href);
    },
    [close, router],
  );

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") close();
    else if (e.key === "ArrowDown") {
      e.preventDefault();
      // Stepped from the CLAMPED cursor, not an updater over the raw value:
      // an updater would wrap from an index the list no longer has, so the
      // first ArrowDown after a list shrank could land anywhere.
      setCursorRaw((cursor + 1) % Math.max(1, ordered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursorRaw((cursor - 1 + ordered.length) % Math.max(1, ordered.length));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(ordered[cursor]);
    }
  }

  if (!open) return null;

  // `i` is the row's position in `ordered`, passed in rather than counted.
  //
  // This used to increment a `let idx` declared outside the function, which
  // worked only because the two groups happened to render in the same order
  // as `ordered` and exactly once per render. Any of React's re-entrant
  // behaviours — a bail-out, a re-render mid-pass, Strict Mode's double
  // invoke — would keep counting from wherever the last pass left it, and
  // every row's highlight would silently address the wrong item.
  const renderRow = (it: SearchItem, i: number) => {
    const active = i === cursor;
    return (
      <button
        key={`${it.kind}-${it.href}`}
        type="button"
        onMouseEnter={() => setCursorRaw(i)}
        onClick={() => go(it)}
        className={`w-full text-left flex items-center gap-3 px-3 py-3 rounded-md ${active ? "bg-bg-2" : ""}`}
      >
        <span
          aria-hidden
          className={`w-2.5 h-2.5 rounded-full flex-none ${it.kind === "read" ? "bg-signal" : "border border-ink-3"}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-medium truncate">{it.name}</span>
          <span className="block text-sm text-ink-3 truncate">{it.desc}</span>
        </span>
        <span className="cs-mono text-ink-3 text-[11px] flex-none">{it.area}</span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-start justify-center px-4 pt-[12vh] bg-ink/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search Calgary Lens"
        className="w-full max-w-[680px] max-h-[72vh] flex flex-col cs-card bg-bg rounded-xl overflow-hidden"
      >
        <div className="flex items-center gap-3 p-3 border-b border-rule">
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-ink-3 flex-none" aria-hidden>
            <circle cx="7" cy="7" r="4.5" />
            <path d="M11 11l3.5 3.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Search a figure, a claim, or a data set…"
            aria-label="Search"
            autoComplete="off"
            className="flex-1 min-w-0 bg-transparent outline-none text-[17px] text-ink placeholder:text-ink-3"
          />
          <button type="button" onClick={close} className="cs-mono text-[11px] text-ink-3 border border-rule rounded px-2 py-1">
            Esc
          </button>
        </div>

        <div className="overflow-y-auto p-2">
          {ordered.length === 0 ? (
            <div className="text-center text-ink-3 py-10">
              No figure, claim, or data set matches <b className="text-ink-2">“{query}”</b>.
            </div>
          ) : (
            <>
              {reads.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 cs-label px-3 pt-3 pb-1">
                    <span aria-hidden className="w-2 h-2 rounded-full bg-signal" />
                    Investigations — the read
                  </div>
                  {reads.map((it, i) => renderRow(it, i))}
                </div>
              )}
              {data.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 cs-label px-3 pt-3 pb-1">
                    <span aria-hidden className="w-2 h-2 rounded-full border border-ink-3" />
                    Data — the record
                  </div>
                  {/* Offset by `reads.length`: `ordered` is [...reads,
                      ...data], so a data row's index continues where the
                      reads left off. This is the arithmetic the old mutable
                      counter was doing implicitly. */}
                  {data.map((it, i) => renderRow(it, reads.length + i))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
